import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { isAuthenticated } from '@/lib/auth';
const NotificationsContext = createContext(null);
/**
 * Global notifications context: the bell badge count + the read mutations.
 * The notifications LIST lives in the page's own paged queries
 * (useNotificationsList) — polling the full list here for every user was
 * wasted work once the page went paginated.
 */
export function NotificationsProvider({ children }) {
    const queryClient = useQueryClient();
    // Dedicated unread-count endpoint: stays correct even though the list is paginated.
    const { data: unread } = useQuery({
        queryKey: ['notifications', 'unread-count'],
        queryFn: () => apiClient('/notifications/unread-count'),
        enabled: isAuthenticated(),
        refetchInterval: 60_000,
    });
    const markRead = useMutation({
        mutationFn: (id) => apiClient(`/notifications/${id}/read`, { method: 'PATCH' }),
        onSuccess: () => {
            // Prefix invalidation covers the unread-count and every paged list tab.
            void queryClient.invalidateQueries({ queryKey: ['notifications'] });
        },
    });
    const markAllRead = useMutation({
        // Backend exposes this as POST (notifications.controller.ts @Post('read-all')).
        mutationFn: () => apiClient('/notifications/read-all', { method: 'POST' }),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['notifications'] });
        },
    });
    return (_jsx(NotificationsContext.Provider, { value: {
            unreadCount: unread?.count ?? 0,
            markAsRead: (id) => markRead.mutate(id),
            markAllAsRead: () => markAllRead.mutate(),
        }, children: children }));
}
export function useNotifications() {
    const ctx = useContext(NotificationsContext);
    if (!ctx)
        throw new Error('useNotifications must be used within NotificationsProvider');
    return ctx;
}
//# sourceMappingURL=NotificationsProvider.js.map